// Shared bits for the Developer Platform tabs: the one-time secret modal,
// the "backend being rolled out" empty state, and small helpers.
import React, { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Copy, Hourglass, Lock, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { getApiErrorMessage } from '@/lib/apiError';

// Always surface the BACKEND error message (across Go {code,message}, FastAPI {detail},
// gateway {error:{message}}, NestJS string[]). The fallback is used only when the backend
// returned nothing human-readable.
export const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

export const inputCls =
  'w-full h-9 px-3 rounded-md border border-border bg-card text-sm disabled:bg-muted disabled:text-muted-foreground';

export const fmtDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export const copyText = async (text: string, okMessage: string, failMessage: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMessage);
  } catch {
    toast.error(failMessage);
  }
};

/** Copy-to-clipboard icon button with a brief "copied" check state. */
export const CopyButton: React.FC<{ text: string; label: string }> = ({ text, label }) => {
  const { t } = useTranslation('admin');
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await copyText(
      text,
      t('developer.copied', { defaultValue: 'Copied to clipboard.' }),
      t('developer.copy_failed', { defaultValue: 'Could not copy — copy it manually.' }),
    );
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={onCopy} aria-label={label}>
      {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
};

/** One-time secret reveal — used for API client secrets AND webhook signing
 * secrets. The secret is never retrievable again, so this modal is the only
 * chance to copy it. */
export const SecretModal: React.FC<{
  title: string;
  description?: string;
  secret: string;
  onClose: () => void;
}> = ({ title, description, secret, onClose }) => {
  const { t } = useTranslation('admin');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2">
            <code className="text-sm font-mono break-all flex-1 text-foreground">{secret}</code>
            <CopyButton text={secret} label={t('developer.copy_secret', { defaultValue: 'Copy secret' })} />
          </div>
          <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm border bg-warning-soft border-warning text-warning-strong">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {t('developer.secret_once', {
                defaultValue: "Copy this secret now and store it somewhere safe — you won't see it again. If you lose it, rotate to get a new one.",
              })}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>{t('developer.secret_done', { defaultValue: 'I have copied it' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Muted empty state for tabs whose backend hasn't been deployed yet. */
export const RolloutPendingState: React.FC<{ message: string }> = ({ message }) => (
  <Card className="border-dashed">
    <CardContent className="py-14 text-center text-muted-foreground">
      <Hourglass className="w-8 h-8 mx-auto mb-3 opacity-50" />
      <p className="text-sm">{message}</p>
    </CardContent>
  </Card>
);

// "Try again" for a tab that hit a temporary failure: RetryScope remounts its
// children, so every tab inside reloads exactly as on first open.
const RetryContext = createContext<(() => void) | null>(null);

export const RetryScope: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [key, setKey] = useState(0);
  return (
    <RetryContext.Provider value={() => setKey((k) => k + 1)}>
      <React.Fragment key={key}>{children}</React.Fragment>
    </RetryContext.Provider>
  );
};

/** Shown when the gateway couldn't confirm the workspace's access just now (503
 * entitlement fallback, DEF-0mvnigi2p001). Temporary: says so, with the
 * gateway's own message and a retry — never "not enabled" or "being rolled out". */
export const TemporarilyUnavailableState: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useTranslation('admin');
  const retry = useContext(RetryContext);
  return (
    <Card className="border-dashed" data-testid="temporarily-unavailable">
      <CardContent className="py-14 text-center">
        <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-muted-foreground opacity-70" />
        <p className="text-sm font-medium text-charcoal">
          {t('developer.unavailable_title', { defaultValue: 'Temporarily unavailable' })}
        </p>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          {message || t('developer.unavailable_hint', { defaultValue: "We couldn't confirm your workspace's access just now. Please try again in a moment." })}
        </p>
        {retry ? (
          <Button variant="outline" size="sm" className="mt-4" onClick={retry}>
            <RefreshCw className="w-4 h-4 mr-1.5" />
            {t('developer.try_again', { defaultValue: 'Try again' })}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
};

/** Shown when the gateway blocks the Developer Platform because the tenant isn't
 * entitled on its current plan (403 feature_not_enabled). Surfaces the backend's
 * own, already-clear message — with an optional upgrade CTA. */
export const FeatureNotEnabledState: React.FC<{ message: string; title?: string }> = ({
  message,
  title,
}) => {
  const { t } = useTranslation('admin');
  return (
    <Card className="border-dashed">
      <CardContent className="py-14 text-center">
        <Lock className="w-8 h-8 mx-auto mb-3 text-muted-foreground opacity-70" />
        <p className="text-sm font-medium text-charcoal">
          {title || t('developer.not_enabled_title', { defaultValue: 'Not available on your current plan' })}
        </p>
        {message ? (
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{message}</p>
        ) : null}
        <p className="mx-auto mt-3 max-w-md text-xs text-muted-foreground">
          {t('developer.not_enabled_hint', {
            defaultValue: 'Ask your account owner to enable the Developer Platform, or contact KaziPeople to add it to your plan.',
          })}
        </p>
      </CardContent>
    </Card>
  );
};
